import React, { type ReactElement, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Roles } from '../../utils/enums/roles.enum';
import UnAuthorized from '../../pages/UnAuthorized/UnAuthorized';
import {
  LOGIN_ROUTE,
  ACCOUNT_LOCKED_ROUTE,
  MY_GEM_VAULT_ROUTE,
  MANAGE_SUBSCRIPTION,
  GEM_GPT_ROUTE,
  POROLIS_GEM_BANK_ROUTE,
  INVITATIONS_ROUTE,
  MY_GEM_FAV_ROUTE,
} from '../../utils/routes';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { Permission } from '../../utils/enums/permission.enum';
import { LayoutType } from '../../utils/enums/layout-type.enum';
import { authActions } from '../../redux/auth/authSlice';
import { useAuth } from '../../context/AuthContext';

export const PermissionRoutes = {
  [MY_GEM_FAV_ROUTE]: [Permission.VIEW_MY_GEM_VAULT, Permission.VIEW_POROLIS_GEM_BANK],
  [MY_GEM_VAULT_ROUTE]: [Permission.VIEW_MY_GEM_VAULT],
  [MANAGE_SUBSCRIPTION]: [Permission.SUBSCRIPTION_MANAGEMENT],
  [GEM_GPT_ROUTE]: [Permission.ACCESS_GEM_GPT],
  [POROLIS_GEM_BANK_ROUTE]: [Permission.VIEW_POROLIS_GEM_BANK],
};

export interface PrivateRouteProp {
  userRole: Roles;
  routerRoles: Roles[];
  permissions?: string[];
  route?: keyof typeof PermissionRoutes;
  DashboardLayout: LayoutType;
  bgColor?: string;
}

export default function PrivateRoute({
  userRole,
  routerRoles,
  permissions,
  route,
  DashboardLayout,
  bgColor,
}: PrivateRouteProp): ReactElement | null {
  const dispatch = useAppDispatch();
  const { accountStatus, componentStatus, previousPath } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const isPageLoading = componentStatus?.isPageLoading ?? true;
  const location = useLocation();
  const { isLogin } = useAuth();

  useEffect(() => {
    if (isLogin) {
      if (previousPath === INVITATIONS_ROUTE) {
        navigate(previousPath);
        dispatch(authActions.clearPreviousPath());
      }
    }
  }, [isLogin, dispatch, previousPath]);

  const handleRouting = () => {
    if (
      route &&
      PermissionRoutes[route] &&
      permissions &&
      (userRole === Roles.SAAS_USER || userRole === Roles.NCC_USER)
    ) {
      if (permissions.some((permission) => PermissionRoutes[route].includes(permission as Permission))) {
        return <Outlet />;
      }

      return <UnAuthorized DashboardLayout={DashboardLayout} bgColor={bgColor} />;
    }
    if (routerRoles.includes(userRole)) {
      return <Outlet />;
    }
    return <UnAuthorized DashboardLayout={DashboardLayout} bgColor={bgColor} />;
  };

  if (!isLogin) {
    const urlParams = new URLSearchParams(location.search);
    const external = urlParams.get('external');
    const path = external ? location.pathname : null;
    return <Navigate to={LOGIN_ROUTE} state={{ previousPath: path }} />;
  }
  if (accountStatus.includes('inactive')) {
    return <Navigate to={ACCOUNT_LOCKED_ROUTE} />;
  }
  if (userRole === Roles.SAAS_ADMIN) {
    return <Outlet />;
  }

  return isPageLoading ? null : handleRouting();
}
